import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Package, AlertTriangle, CheckCircle } from 'lucide-react';

interface ItemQuality {
  id: string;
  procurement_item_id: string;
  quality_grade?: string;
  damage_kg?: number;
  compensation_kg?: number;
  damage_percentage?: number;
  preliminary_damage_kg?: number;
  preliminary_compensation_kg?: number;
  preliminary_damage_percentage?: number;
  grade_notes?: string;
  is_rejected?: boolean;
}

interface ProcurementItem {
  id: string;
  product_name: string;
  quantity: number;
  quality_grade?: string;
}

interface ItemQualityReportCardProps {
  items: ProcurementItem[];
  itemQualities: ItemQuality[];
  isPreliminary?: boolean;
  isFinalComplete?: boolean;
}

const ItemQualityReportCard: React.FC<ItemQualityReportCardProps> = ({
  items,
  itemQualities,
  isPreliminary = false,
  isFinalComplete = false,
}) => {
  // Calculate totals
  const totals = itemQualities.reduce(
    (acc, iq) => ({
      damageKg: acc.damageKg + (iq.damage_kg || 0),
      compensationKg: acc.compensationKg + (iq.compensation_kg || 0),
      prelimDamageKg: acc.prelimDamageKg + (iq.preliminary_damage_kg || 0),
      prelimCompensationKg: acc.prelimCompensationKg + (iq.preliminary_compensation_kg || 0),
    }),
    { damageKg: 0, compensationKg: 0, prelimDamageKg: 0, prelimCompensationKg: 0 }
  );

  const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);

  const getGradeBadgeColor = (grade?: string) => {
    if (!grade) return 'bg-muted text-muted-foreground';
    switch (grade.toLowerCase()) {
      case 'a':
      case 'grade a':
        return 'bg-green-100 text-green-800';
      case 'b':
      case 'grade b':
        return 'bg-blue-100 text-blue-800';
      case 'c':
      case 'grade c':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getDamageSeverity = (damageKg: number, quantity: number) => {
    if (damageKg === 0) return 'none';
    const percentage = (damageKg / quantity) * 100;
    if (percentage < 5) return 'low';
    if (percentage < 15) return 'moderate';
    return 'high';
  };

  return (
    <Card className="border-blue-200 bg-blue-50/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Quality Inspection by Product ({items.length} items)
          {isPreliminary && !isFinalComplete && (
            <Badge variant="secondary" className="bg-amber-100 text-amber-800">
              Preliminary
            </Badge>
          )}
          {isFinalComplete && (
            <Badge className="bg-green-600">
              <CheckCircle className="h-3 w-3 mr-1" />
              Final Complete
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Per-Item Quality Reports */}
        <div className="space-y-3">
        {items.map((item) => {
            const itemQuality = itemQualities.find(
              (iq) => iq.procurement_item_id === item.id
            );
            const isRejected = itemQuality?.is_rejected === true;
            const damageKg = isPreliminary && !isFinalComplete
              ? itemQuality?.preliminary_damage_kg || 0
              : itemQuality?.damage_kg || 0;
            const compensationKg = isPreliminary && !isFinalComplete
              ? itemQuality?.preliminary_compensation_kg || 0
              : itemQuality?.compensation_kg || 0;
            const severity = getDamageSeverity(damageKg, item.quantity);
            const netPayable = isRejected ? 0 : Math.max(0, item.quantity - compensationKg);

            return (
              <div
                key={item.id}
                className={`border rounded-lg p-3 ${
                  isRejected
                    ? 'bg-destructive/10 border-destructive'
                    : severity === 'high'
                    ? 'bg-background border-destructive/50'
                    : severity === 'moderate'
                    ? 'bg-background border-amber-300'
                    : 'bg-background border-border'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${isRejected ? 'line-through text-muted-foreground' : ''}`}>
                      {item.product_name}
                    </span>
                    {isRejected ? (
                      <Badge variant="destructive" className="flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Rejected
                      </Badge>
                    ) : (
                      <Badge className={getGradeBadgeColor(itemQuality?.quality_grade || item.quality_grade)}>
                        {itemQuality?.quality_grade || item.quality_grade || 'N/A'}
                      </Badge>
                    )}
                  </div>
                  {!isRejected && severity !== 'none' && (
                    <div className="flex items-center gap-1 text-xs">
                      {severity === 'high' && (
                        <AlertTriangle className="h-3 w-3 text-destructive" />
                      )}
                      <span
                        className={
                          severity === 'high'
                            ? 'text-destructive'
                            : severity === 'moderate'
                            ? 'text-amber-600'
                            : 'text-muted-foreground'
                        }
                      >
                        {severity === 'high' ? 'High damage' : severity === 'moderate' ? 'Moderate damage' : ''}
                      </span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">Quantity</Label>
                    <p className="font-medium">{item.quantity} KG</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Damage</Label>
                    <p className={`font-medium ${damageKg > 0 ? 'text-destructive' : ''}`}>
                      {damageKg} KG
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Compensation</Label>
                    <p className={`font-medium ${compensationKg > 0 ? 'text-orange-600' : ''}`}>
                      {compensationKg} KG
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Net Payable</Label>
                    <p className="font-medium text-green-700">{netPayable.toFixed(2)} KG</p>
                  </div>
                </div>

                {itemQuality?.grade_notes && (
                  <div className="mt-2 pt-2 border-t">
                    <Label className="text-xs text-muted-foreground">Notes</Label>
                    <p className="text-xs text-muted-foreground">{itemQuality.grade_notes}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary Totals */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <h4 className="font-medium text-green-800 mb-2">Summary Totals</h4>
          <div className="grid grid-cols-4 gap-2 text-sm">
            <div>
              <Label className="text-xs text-green-700">Total Quantity</Label>
              <p className="font-bold text-green-800">{totalQuantity.toFixed(2)} KG</p>
            </div>
            <div>
              <Label className="text-xs text-green-700">Total Damage</Label>
              <p className="font-bold text-destructive">
                {isPreliminary && !isFinalComplete
                  ? totals.prelimDamageKg.toFixed(2)
                  : totals.damageKg.toFixed(2)}{' '}
                KG
              </p>
            </div>
            <div>
              <Label className="text-xs text-green-700">Total Compensation</Label>
              <p className="font-bold text-orange-600">
                {isPreliminary && !isFinalComplete
                  ? totals.prelimCompensationKg.toFixed(2)
                  : totals.compensationKg.toFixed(2)}{' '}
                KG
              </p>
            </div>
            <div>
              <Label className="text-xs text-green-700">Net Payable</Label>
              <p className="font-bold text-green-700">
                {(
                  totalQuantity -
                  (isPreliminary && !isFinalComplete
                    ? totals.prelimCompensationKg
                    : totals.compensationKg)
                ).toFixed(2)}{' '}
                KG
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ItemQualityReportCard;
